import { Grid } from "@mui/material";
import { useCallback, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UNABLE_TO_CONTINUE_CLEAR_CONFLICTS } from "../../../consts/strings";
import { Modal } from "../Modal";
import { ConfirmDialog } from "../../ConfirmDialog";
import { WarningIcon } from "../../Icons";
import { NewOverrideActions } from "./NewOverrideComponents/NewOverrideActions";
import { NewOverrideContent } from "./NewOverrideComponents/NewOverrideContent";
import { ViewOverridesActions } from "./ViewOverrideComponents/ViewOverridesActions";
import { ViewOverridesContent } from "./ViewOverrideComponents/ViewOverridesContent";
import { editOverridesModalOpenSelector } from "../../../reducers/ownApp/selectors";
import {
  editModalOverridesSelector,
  routeInEditSelector,
} from "../../../reducers/matrixProfile/selectors";
import {
  applyOverridesEdit,
  cancelOverridesEdit,
} from "../../../reducers/matrixProfile/thunkActions";
import { getOverridesWithConflicts } from "../../../utils/overrides";

export const EditOverridesModal = () => {
  const routeEdit = useSelector(routeInEditSelector);
  const modalOpen = useSelector(editOverridesModalOpenSelector);
  const editModalOverrides = useSelector(editModalOverridesSelector);
  const editOverrides = editModalOverrides?.editOverrides;
  const [createNew, setCreateNew] = useState(false);
  const [validationMap, setValidationMap] = useState();
  const [hasConflicts, setHasConflicts] = useState(false);
  const [prevEditOverrides, setPrevEditOverrides] = useState();
  const [hasChanges, setHasChanges] = useState(false);
  const dispatch = useDispatch();

  const isInputValid = !Object.values(validationMap || {}).includes(false);

  const onClose = useCallback(() => {
    dispatch(cancelOverridesEdit());
    setValidationMap(null);
    setCreateNew(false);
  }, [dispatch]);

  const checkConflicts = useCallback(() => {
    const { newOverrides, editOverrides } = editModalOverrides;
    const overridesWithConflicts = getOverridesWithConflicts(
      Object.values({
        ...editOverrides,
        ...newOverrides,
      })
    ).filter(({ conflict }) => conflict);
    return !!overridesWithConflicts.length;
  }, [editModalOverrides]);

  const onSave = useCallback(() => {
    if (checkConflicts()) return setHasConflicts(true);
    dispatch(applyOverridesEdit());
    setValidationMap(null);
    setCreateNew(false);
  }, [dispatch, checkConflicts]);

  const onValidated = useCallback(({ overrideUuid, isValid }) => {
    setValidationMap({
      [overrideUuid]: isValid,
    });
  }, []);

  const onNewOverridePress = useCallback(() => {
    if (checkConflicts()) return setHasConflicts(true);
    else setCreateNew(true);
  }, [checkConflicts]);

  useEffect(() => {
    if (prevEditOverrides === editOverrides) return;
    setPrevEditOverrides(editOverrides);
    if (
      !prevEditOverrides &&
      editOverrides &&
      !Object.values(editOverrides).length
    ) {
      onNewOverridePress();
    }
  }, [prevEditOverrides, editOverrides, onNewOverridePress]);

  useEffect(() => {
    setHasChanges(false);
  }, [modalOpen]);

  if (!!!routeEdit) return null;

  return (
    <Modal
      open={modalOpen}
      title={`Overrides: Route ${routeEdit.routeCode}`}
      minWidth={700}
      onClose={onClose}
      content={
        <>
          {createNew ? (
            <NewOverrideContent
              hasConflicts={hasConflicts}
              onValidated={onValidated}
            />
          ) : (
            <ViewOverridesContent
              hasConflicts={hasConflicts}
              onValidated={onValidated}
              overrides={editOverrides}
              onEdit={() => setHasChanges(true)}
            />
          )}
          <ConfirmDialog
            open={!!hasConflicts}
            onConfirm={() => setHasConflicts(false)}
          >
            <Grid container>
              <Grid item xs={2}>
                <WarningIcon />
              </Grid>
              <Grid item xs={10}>
                {UNABLE_TO_CONTINUE_CLEAR_CONFLICTS}
              </Grid>
            </Grid>
          </ConfirmDialog>
        </>
      }
      actions={
        createNew ? (
          <NewOverrideActions
            disabled={!isInputValid}
            onCancel={onClose}
            onSave={onSave}
          />
        ) : (
          <ViewOverridesActions
            disabled={!isInputValid}
            onCancel={onClose}
            onSave={onSave}
            disabledNewOverride={!isInputValid || hasChanges}
            onNewOverride={onNewOverridePress}
          />
        )
      }
    />
  );
};
